"use client"

import { cn } from "@/lib/utils"
import type { PageConfig } from "./types"
import Image from "next/image"
import { Trash2, Copy, Plus, FileText } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"

interface PageThumbnailProps {
  page: PageConfig
  pageNumber: number
  selected?: boolean
  disabled?: boolean
  onClick: () => void
  onDelete?: () => void
  onDuplicate?: () => void
}

export function PageThumbnail({
  page,
  pageNumber,
  selected = false,
  disabled = false,
  onClick,
  onDelete,
  onDuplicate,
}: PageThumbnailProps) {
  const aspectRatio = page.width / page.height
  const thumbnailHeight = 140
  const thumbnailWidth = thumbnailHeight * aspectRatio

  return (
    <div
      className={cn(
        "group relative flex flex-col items-center gap-2 p-2 rounded-lg cursor-pointer transition-all",
        selected
          ? "bg-primary/10 ring-2 ring-primary shadow-sm"
          : "hover:bg-muted/80 hover:shadow-sm",
        disabled && "opacity-50 cursor-not-allowed"
      )}
      onClick={disabled ? undefined : onClick}
    >
      <div
        className={cn(
          "relative bg-white shadow-md border rounded overflow-hidden transition-transform",
          selected && "scale-[1.02]",
          !disabled && "group-hover:shadow-lg"
        )}
        style={{
          width: thumbnailWidth,
          height: thumbnailHeight,
        }}
      >
        {page.items.map((item) => {
          const scaleX = thumbnailWidth / page.width
          const scaleY = thumbnailHeight / page.height
          return (
            <div
              key={item.id}
              className="absolute"
              style={{
                left: item.position.x * scaleX,
                top: item.position.y * scaleY,
                width: item.size.width * scaleX,
                height: item.size.height * scaleY,
                transform: `rotate(${item.rotation}deg)`,
              }}
            >
              <Image
                src={item.src}
                alt={item.fileName}
                fill
                className="object-contain"
                style={{
                  transform: `${item.flipX ? "scaleX(-1)" : ""} ${item.flipY ? "scaleY(-1)" : ""}`.trim() || undefined,
                  opacity: (item.opacity ?? 100) / 100,
                }}
                draggable={false}
              />
            </div>
          )
        })}

        {page.items.length === 0 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground/50 gap-1">
            <FileText className="h-6 w-6" />
            <span className="text-[10px]">Empty page</span>
          </div>
        )}

        {!disabled && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors" />
        )}
      </div>

      <div className="flex items-center gap-1.5">
        <Badge
          variant={selected ? "default" : "secondary"}
          className={cn("text-[10px] px-1.5 py-0 h-5", selected && "bg-primary")}
        >
          {pageNumber}
        </Badge>
        {page.items.length > 0 && (
          <span className="text-[10px] text-muted-foreground">
            {page.items.length} {page.items.length === 1 ? "image" : "images"}
          </span>
        )}
      </div>

      {!disabled && (
        <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {onDuplicate && (
            <Button
              variant="secondary"
              size="icon"
              className="h-6 w-6 shadow-sm"
              onClick={(e) => {
                e.stopPropagation()
                onDuplicate()
              }}
            >
              <Copy className="h-3 w-3" />
            </Button>
          )}
          {onDelete && (
            <Button
              variant="secondary"
              size="icon"
              className="h-6 w-6 shadow-sm text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={(e) => {
                e.stopPropagation()
                onDelete()
              }}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

interface PageListProps {
  pages: PageConfig[]
  selectedPageIndex: number
  disabled?: boolean
  onSelectPage: (index: number) => void
  onDeletePage: (index: number) => void
  onDuplicatePage: (index: number) => void
  onAddPage: () => void
}

export function PageList({
  pages,
  selectedPageIndex,
  disabled = false,
  onSelectPage,
  onDeletePage,
  onDuplicatePage,
  onAddPage,
}: PageListProps) {
  const totalImages = pages.reduce((sum, page) => sum + page.items.length, 0)

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30">
        <div>
          <h3 className="text-sm font-semibold">Pages</h3>
          <p className="text-[10px] text-muted-foreground">
            {pages.length} {pages.length === 1 ? "page" : "pages"} · {totalImages} {totalImages === 1 ? "image" : "images"}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs gap-1"
          onClick={onAddPage}
          disabled={disabled}
        >
          <Plus className="h-3.5 w-3.5" />
          Add
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {pages.map((page, index) => (
          <PageThumbnail
            key={page.id}
            page={page}
            pageNumber={index + 1}
            selected={index === selectedPageIndex}
            disabled={disabled}
            onClick={() => onSelectPage(index)}
            onDelete={pages.length > 1 ? () => onDeletePage(index) : undefined}
            onDuplicate={() => onDuplicatePage(index)}
          />
        ))}
      </div>
    </div>
  )
}
